import type { DoctrineId } from '../data/doctrines.js';
import type { MapId } from '../data/maps/index.js';
import type { PlayerView } from '../matchHost/MatchHost.js';
import type { MatchOutcome, MatchState } from '../simulation/State.js';
import type { PlayerId, Tick } from '../simulation/ids.js';

// Pure telemetry helpers for V1.5 playtest. The row shape and the
// summarize aggregator live here so both the browser tester
// (apps/client/src/save/Telemetry.ts) and the headless playtest runner
// (tools/playtest-auto.ts) can produce / consume the same MatchTelemetryRow
// without either side depending on DOM, localStorage, or Phaser
// (Rule 7). The browser-only persistence layer keeps those concerns and
// re-exports the row + summarize symbols from here.

export interface MatchTelemetryRow {
  // ISO 8601 wall-clock at row write time (post-match). Excluded from
  // simulation determinism — purely a "when did this match happen" tag.
  recordedAt: string;
  mapId: MapId;
  // Sim seconds elapsed when the match ended. Easier to read than ticks.
  matchSeconds: number;
  matchLengthSec: number;
  // Outcome shape mirrors MatchOutcome — kind / winner / reason / tick.
  outcome: MatchOutcome;
  // Per-player snapshot at end-of-match.
  doctrines: [DoctrineId | null, DoctrineId | null];
  // null = doctrine was auto-assigned at window close (player ignored
  // the picker); a Tick = explicit pick at that tick.
  doctrinePickedAtTick: [Tick | null, Tick | null];
  signalScore: [number, number];
  peakIncomePerSec: [number, number];
  squadsBuilt: [number, number];
  // Counters from MatchStats (V1.5).
  underdogSurgeSecondsByPlayer: [number, number];
  underdogSurgeActivationsByPlayer: [number, number];
  isolatedRelaySecondsByPlayer: [number, number];
  campTier1ClearedAtTick: Tick | null;
  campTier2ClearedAtTick: Tick | null;
  cachesPickedByPlayer: [number, number];
  // Camp damage attribution (added on top of cache pickup): who actually
  // did the work to clear each tier, vs. who walked over the reward.
  campTier1DamageByPlayer: [number, number];
  campTier2DamageByPlayer: [number, number];
  campTier1ClearedByPlayer: PlayerId | null;
  campTier2ClearedByPlayer: PlayerId | null;
  campTier1ClearedDurationSec: number | null;
  campTier2ClearedDurationSec: number | null;
  // V1.5+ forward turret network dependency telemetry. Per-player accumulated
  // seconds spent with `isOnline: false` mini-turrets. Surfaces "how often
  // did relay flips disable forward pressure?" in the playtest summary.
  forwardTurretOfflineSecondsByPlayer: [number, number];
  // V1.5+ Slice 2 — Cut Link telemetry. Counts every successful channel
  // completion per player, plus the cumulative seconds each player kept any
  // relay in a cut window. Surfaces "is Cut Link landing? Is it landing
  // long?" in the bot-vs-bot summary the §17 verification reads.
  cutLinksAppliedByPlayer: [number, number];
  cutLinkActiveSecondsByPlayer: [number, number];
  // V1.5+ Slice 6 — Disrupt Uplink telemetry. Counts every successful
  // channel completion per player; cumulative seconds each player kept any
  // relay in a disruption window. Mirrors cut-link / jam-relay shape so
  // the playtest summary renders the disruption story side-by-side.
  disruptUplinksAppliedByPlayer: [number, number];
  disruptUplinkActiveSecondsByPlayer: [number, number];
  // V1.5+ Slice 7 — Fortify Relay telemetry. Counts every successful
  // channel completion per player; cumulative seconds each player kept any
  // relay in a fortify window. Surfaces "is the defensive primitive
  // landing? Is uptime correlated with falling relay-flip rate?" in the
  // §17 verification.
  fortifyRelaysAppliedByPlayer: [number, number];
  fortifyRelayActiveSecondsByPlayer: [number, number];
  // V1.5+ telemetry pre-flight — per-player count of relay ownership flips
  // (neutral → owned and owned-by-X → owned-by-Y, attributed to the new
  // owner). The §17 anti-turtling gate watches this aggregate: if a future
  // change LOWERS the average per-match flip count, it has made turtling
  // easier and must be reverted or rebalanced.
  relayFlipsByPlayer: [number, number];
  // V1.5+ Slice 8 — Signal Storm Endgame. Per-player count of flips that
  // landed during the storm window (subset of relayFlipsByPlayer). The §17
  // final-minute requirement is closed by this metric; this slice's
  // pre/post comparison watches `relayFlipsDuringStorm` (a sum of these)
  // specifically as the load-bearing metric, not aggregate flip rate.
  signalStormFlipsByPlayer: [number, number];
}

export interface TelemetrySummary {
  matches: number;
  doctrinePickRate: Record<DoctrineId, number>;
  doctrineAutoAssignRate: number;
  averageMatchSeconds: number;
  matchesWithAnySurge: number;
  matchesWithIsolatedRelays: number;
  // V1.5+ Slice 2 — number of matches where at least one Cut Link landed,
  // and the per-player aggregate of completed cuts. The §17 verification
  // wants to see Cut Link activity correlate with shorter average match
  // length / higher relay flip rate; this surfaces both halves.
  matchesWithCutLinks: number;
  totalCutLinksApplied: number;
  // V1.5+ Slice 6 — number of matches with at least one Disrupt Uplink
  // landed, and the per-player aggregate of completed disrupts. The §17
  // verification wants to see Disrupt Uplink correlate with shorter
  // average match length AND ≥-baseline relay-flip rate.
  matchesWithDisruptUplinks: number;
  totalDisruptUplinksApplied: number;
  // V1.5+ Slice 7 — number of matches with at least one Fortify Relay
  // landed, plus per-player aggregate of completed fortifies. The §17
  // verification wants to see fortify activity NOT push relay-flip rate
  // below the post-Slice-6 baseline (7.0). If matchesWithFortify is high
  // AND averageRelayFlipsPerMatch is low, the slice has enabled
  // turtling-lite and should be retuned (longer cooldown, shorter window).
  matchesWithFortifyRelays: number;
  totalFortifyRelaysApplied: number;
  totalCachesPicked: number;
  // V1.5+ telemetry pre-flight — average per-match relay flips (sum of both
  // players' flip counts, divided by match count). The §17 gate compares
  // this number before/after a change.
  averageRelayFlipsPerMatch: number;
  // V1.5+ Slice 8 — Signal Storm Endgame. Average per-match flips that
  // landed during the storm window. The §17 final-minute requirement
  // gate: this number must be ≥ 0.8 per match (the storm must produce
  // at least one meaningful flip in the 60s window across the bot-vs-bot
  // population). Distinct from averageRelayFlipsPerMatch: a storm that
  // shifts existing flips into the 60s window passes this gate even if
  // aggregate flips stay flat; a storm that just decorates with no
  // mechanical impact would fail it.
  relayFlipsDuringStorm: number;
  outcomesByKind: Record<string, number>;
  // Camp attribution rollups. mismatchCount = matches where a tier's
  // CLEARER (most damage) and PICKER (cache collector) are different
  // players. A non-trivial number is interesting telemetry: the camp's
  // reward isn't always going to whoever did the work.
  tier1ClearedByMatches: { 0: number; 1: number; tied: number };
  tier2ClearedByMatches: { 0: number; 1: number; tied: number };
  tier1ClearVsPickMismatch: number;
  tier2ClearVsPickMismatch: number;
  averageTier1ClearDurationSec: number | null;
  averageTier2ClearDurationSec: number | null;
}

// buildTelemetryRow extracts the canonical row from a terminal snapshot.
// "Terminal" means state.outcome !== null — calling on a live match throws
// because the row's outcome field is non-nullable by design.
export function buildTelemetryRow(
  state: PlayerView | MatchState,
  recordedAt: string = new Date().toISOString(),
): MatchTelemetryRow {
  if (state.outcome === null) {
    throw new Error('telemetry: buildTelemetryRow called on a non-terminal state');
  }
  return {
    recordedAt,
    mapId: state.mapId,
    matchSeconds: state.matchSeconds,
    matchLengthSec: state.matchLengthSec,
    outcome: state.outcome,
    doctrines: [state.players[0].doctrineId, state.players[1].doctrineId],
    doctrinePickedAtTick: [
      state.stats.doctrinePickedAtTick[0],
      state.stats.doctrinePickedAtTick[1],
    ],
    signalScore: [state.players[0].signalScore, state.players[1].signalScore],
    peakIncomePerSec: [
      state.players[0].peakIncomePerSec,
      state.players[1].peakIncomePerSec,
    ],
    squadsBuilt: [state.stats.squadsBuilt[0], state.stats.squadsBuilt[1]],
    underdogSurgeSecondsByPlayer: [
      state.stats.underdogSurgeSecondsByPlayer[0],
      state.stats.underdogSurgeSecondsByPlayer[1],
    ],
    underdogSurgeActivationsByPlayer: [
      state.stats.underdogSurgeActivationsByPlayer[0],
      state.stats.underdogSurgeActivationsByPlayer[1],
    ],
    isolatedRelaySecondsByPlayer: [
      state.stats.isolatedRelaySecondsByPlayer[0],
      state.stats.isolatedRelaySecondsByPlayer[1],
    ],
    campTier1ClearedAtTick: state.stats.campTier1ClearedAtTick,
    campTier2ClearedAtTick: state.stats.campTier2ClearedAtTick,
    cachesPickedByPlayer: [
      state.stats.cachesPickedByPlayer[0],
      state.stats.cachesPickedByPlayer[1],
    ],
    campTier1DamageByPlayer: [
      state.stats.campTier1DamageByPlayer[0],
      state.stats.campTier1DamageByPlayer[1],
    ],
    campTier2DamageByPlayer: [
      state.stats.campTier2DamageByPlayer[0],
      state.stats.campTier2DamageByPlayer[1],
    ],
    campTier1ClearedByPlayer: state.stats.campTier1ClearedByPlayer,
    campTier2ClearedByPlayer: state.stats.campTier2ClearedByPlayer,
    campTier1ClearedDurationSec: state.stats.campTier1ClearedDurationSec,
    campTier2ClearedDurationSec: state.stats.campTier2ClearedDurationSec,
    forwardTurretOfflineSecondsByPlayer: [
      state.stats.forwardTurretOfflineSecondsByPlayer[0],
      state.stats.forwardTurretOfflineSecondsByPlayer[1],
    ],
    cutLinksAppliedByPlayer: [
      state.stats.cutLinksAppliedByPlayer[0],
      state.stats.cutLinksAppliedByPlayer[1],
    ],
    cutLinkActiveSecondsByPlayer: [
      state.stats.cutLinkActiveSecondsByPlayer[0],
      state.stats.cutLinkActiveSecondsByPlayer[1],
    ],
    disruptUplinksAppliedByPlayer: [
      state.stats.disruptUplinksAppliedByPlayer[0],
      state.stats.disruptUplinksAppliedByPlayer[1],
    ],
    disruptUplinkActiveSecondsByPlayer: [
      state.stats.disruptUplinkActiveSecondsByPlayer[0],
      state.stats.disruptUplinkActiveSecondsByPlayer[1],
    ],
    fortifyRelaysAppliedByPlayer: [
      state.stats.fortifyRelaysAppliedByPlayer[0],
      state.stats.fortifyRelaysAppliedByPlayer[1],
    ],
    fortifyRelayActiveSecondsByPlayer: [
      state.stats.fortifyRelayActiveSecondsByPlayer[0],
      state.stats.fortifyRelayActiveSecondsByPlayer[1],
    ],
    relayFlipsByPlayer: [
      state.stats.relayFlipsByPlayer[0],
      state.stats.relayFlipsByPlayer[1],
    ],
    signalStormFlipsByPlayer: [
      state.stats.signalStormFlipsByPlayer[0],
      state.stats.signalStormFlipsByPlayer[1],
    ],
  };
}

export function summarizeRows(rows: readonly MatchTelemetryRow[]): TelemetrySummary {
  const doctrineCounts: Record<DoctrineId, number> = {
    vanguard: 0,
    saboteur: 0,
    bulwark: 0,
  };
  let doctrineSlots = 0;
  let autoAssigned = 0;
  let totalSeconds = 0;
  let surgeMatches = 0;
  let isolatedMatches = 0;
  let cutLinkMatches = 0;
  let totalCutLinks = 0;
  let disruptUplinkMatches = 0;
  let totalDisruptUplinks = 0;
  let fortifyRelayMatches = 0;
  let totalFortifyRelays = 0;
  let totalCaches = 0;
  let totalRelayFlips = 0;
  let totalStormFlips = 0;
  const outcomesByKind: Record<string, number> = {};
  const tier1ClearedByMatches = { 0: 0, 1: 0, tied: 0 };
  const tier2ClearedByMatches = { 0: 0, 1: 0, tied: 0 };
  let tier1Mismatch = 0;
  let tier2Mismatch = 0;
  let tier1DurationSum = 0;
  let tier1DurationCount = 0;
  let tier2DurationSum = 0;
  let tier2DurationCount = 0;

  for (const r of rows) {
    totalSeconds += r.matchSeconds;
    for (const id of [0, 1] as const) {
      const d = r.doctrines[id];
      if (d !== null) {
        doctrineCounts[d] += 1;
        doctrineSlots += 1;
      }
      if (d !== null && r.doctrinePickedAtTick[id] === null) autoAssigned += 1;
    }
    if (r.underdogSurgeActivationsByPlayer[0] + r.underdogSurgeActivationsByPlayer[1] > 0) {
      surgeMatches += 1;
    }
    if (r.isolatedRelaySecondsByPlayer[0] + r.isolatedRelaySecondsByPlayer[1] > 0) {
      isolatedMatches += 1;
    }
    const cuts = r.cutLinksAppliedByPlayer[0] + r.cutLinksAppliedByPlayer[1];
    if (cuts > 0) cutLinkMatches += 1;
    totalCutLinks += cuts;
    // V1.5+ Slice 6: older rows persisted in localStorage (pre-Slice-6) lack
    // disruptUplinksAppliedByPlayer; null-coalesce at the storage boundary
    // (Rule 13 — same shim shape the pre-flight slice used for
    // relayFlipsByPlayer).
    const disrupts = r.disruptUplinksAppliedByPlayer
      ? r.disruptUplinksAppliedByPlayer[0] + r.disruptUplinksAppliedByPlayer[1]
      : 0;
    if (disrupts > 0) disruptUplinkMatches += 1;
    totalDisruptUplinks += disrupts;
    // V1.5+ Slice 7: same null-coalesce shim for pre-Slice-7 persisted rows.
    const fortifies = r.fortifyRelaysAppliedByPlayer
      ? r.fortifyRelaysAppliedByPlayer[0] + r.fortifyRelaysAppliedByPlayer[1]
      : 0;
    if (fortifies > 0) fortifyRelayMatches += 1;
    totalFortifyRelays += fortifies;
    totalCaches += r.cachesPickedByPlayer[0] + r.cachesPickedByPlayer[1];
    // Older rows persisted in localStorage (pre-V1.5+-pre-flight) lack
    // relayFlipsByPlayer; treat them as zero rather than crashing the
    // aggregator (Rule 13 — no shims, but a missing-field guard for
    // already-persisted browser data is the correct read of the field's
    // optionality at the storage boundary).
    const flips = r.relayFlipsByPlayer ?? [0, 0];
    totalRelayFlips += flips[0] + flips[1];
    // V1.5+ Slice 8: same null-coalesce shim for older rows persisted in
    // localStorage before this slice — they lack signalStormFlipsByPlayer;
    // treat as zero (Rule 13 — no shims, but a missing-field guard at the
    // storage boundary is the correct read of the field's optionality).
    const stormFlips = r.signalStormFlipsByPlayer ?? [0, 0];
    totalStormFlips += stormFlips[0] + stormFlips[1];
    outcomesByKind[r.outcome.kind] = (outcomesByKind[r.outcome.kind] ?? 0) + 1;

    accumulateClearAttribution(
      r.campTier1ClearedByPlayer,
      r.campTier1ClearedAtTick,
      r.cachesPickedByPlayer,
      tier1ClearedByMatches,
      (mismatch) => { if (mismatch) tier1Mismatch += 1; },
    );
    accumulateClearAttribution(
      r.campTier2ClearedByPlayer,
      r.campTier2ClearedAtTick,
      r.cachesPickedByPlayer,
      tier2ClearedByMatches,
      (mismatch) => { if (mismatch) tier2Mismatch += 1; },
    );
    if (r.campTier1ClearedDurationSec !== null) {
      tier1DurationSum += r.campTier1ClearedDurationSec;
      tier1DurationCount += 1;
    }
    if (r.campTier2ClearedDurationSec !== null) {
      tier2DurationSum += r.campTier2ClearedDurationSec;
      tier2DurationCount += 1;
    }
  }
  const denom = doctrineSlots === 0 ? 1 : doctrineSlots;
  return {
    matches: rows.length,
    doctrinePickRate: {
      vanguard: doctrineCounts.vanguard / denom,
      saboteur: doctrineCounts.saboteur / denom,
      bulwark: doctrineCounts.bulwark / denom,
    },
    doctrineAutoAssignRate: doctrineSlots === 0 ? 0 : autoAssigned / doctrineSlots,
    averageMatchSeconds: rows.length === 0 ? 0 : totalSeconds / rows.length,
    matchesWithAnySurge: surgeMatches,
    matchesWithIsolatedRelays: isolatedMatches,
    matchesWithCutLinks: cutLinkMatches,
    totalCutLinksApplied: totalCutLinks,
    matchesWithDisruptUplinks: disruptUplinkMatches,
    totalDisruptUplinksApplied: totalDisruptUplinks,
    matchesWithFortifyRelays: fortifyRelayMatches,
    totalFortifyRelaysApplied: totalFortifyRelays,
    totalCachesPicked: totalCaches,
    averageRelayFlipsPerMatch: rows.length === 0 ? 0 : totalRelayFlips / rows.length,
    relayFlipsDuringStorm: rows.length === 0 ? 0 : totalStormFlips / rows.length,
    outcomesByKind,
    tier1ClearedByMatches,
    tier2ClearedByMatches,
    tier1ClearVsPickMismatch: tier1Mismatch,
    tier2ClearVsPickMismatch: tier2Mismatch,
    averageTier1ClearDurationSec:
      tier1DurationCount === 0 ? null : tier1DurationSum / tier1DurationCount,
    averageTier2ClearDurationSec:
      tier2DurationCount === 0 ? null : tier2DurationSum / tier2DurationCount,
  };
}

// The "mismatch" call only fires when a tier WAS cleared AND a cache WAS
// picked (a single pick) by a different player than the clearer. The
// row's flat cachesPickedByPlayer can't disambiguate per-tier, so we
// only attribute when the totals make a single tier's attribution
// unambiguous.
function accumulateClearAttribution(
  clearedBy: PlayerId | null,
  clearedAtTick: Tick | null,
  cachesPickedByPlayer: readonly [number, number],
  buckets: { 0: number; 1: number; tied: number },
  onMismatch: (mismatch: boolean) => void,
): void {
  if (clearedAtTick === null) return;
  if (clearedBy === null) {
    buckets.tied += 1;
    return;
  }
  buckets[clearedBy] += 1;
  const totalPicks = cachesPickedByPlayer[0] + cachesPickedByPlayer[1];
  if (totalPicks !== 1) return;
  const picker: PlayerId = cachesPickedByPlayer[0] > cachesPickedByPlayer[1] ? 0 : 1;
  onMismatch(picker !== clearedBy);
}
